// 공통 API 클라이언트 — 인증 헤더 부착 + JSON 요청 + multipart(폼) 요청.
//
// 배경: fetchJson / authHeaders / fetchJsonForm 이 여러 API 파일(crm.ts·policyApi.ts·
// catalogApi.ts·adminImport.ts)에 거의 같은 모양으로 복붙돼 있었다. 이 모듈이 그 공통
// 동작(Supabase 세션 토큰 Bearer 첨부 → fetch → 실패 시 apiError 변환 → res.json())을
// 한 곳으로 모은다. 각 파일은 자기 export 시그니처를 유지한 채 내부 구현만 여기에 위임한다.
//
// 흡수한 4곳의 차이:
//   - Content-Type 자동 설정 시점: crm/adminImport 은 body 가 truthy 면, policy 는 body 가
//     string 일 때만, catalog 는 body 가 undefined 가 아닐 때. → contentTypeMode 옵션으로 흡수.
//   - multipart: policy 의 fetchJsonForm 처럼 FormData 를 그대로 보내고 Content-Type 은
//     브라우저(boundary)에 맡긴다. → buildAuthHeaders 로 Authorization 만 싣고 직접 fetch.

import { supabase } from "../lib/supabase";
import { getCurrentActionId, recordRequest } from "../lib/perf-trace";
import { getRouteRequestSignal } from "../lib/route-request-scope";
import { ApiError, apiError, toNetworkError } from "./_error";
import * as Sentry from "@sentry/react";

/**
 * Supabase 세션이 있으면 Bearer 토큰을 실은 Headers 를 만든다. init.headers 는 보존·병합.
 *
 * 쓸 수 있는 세션이 없으면(저장소가 잠깐 빈 찰나 / 토큰이 이미 만료) **보내기 전에 한 번 복구**한다.
 * 이 클라이언트(authJson·authFetch·authForm)를 타는 요청은 전부 로그인이 필요한 API 라
 * (공개 입력폼은 publicFormHeaders + 생 fetch 로 따로 나간다) 토큰 없이 보내면 401 이 확정이고,
 * 그 401 은 fetchWithAuthRetry 의 재시도로도 못 살린다 — 재시도는 "세션은 있는데 토큰만 만료"를
 * 전제로 갱신하는데, 세션 자체가 비어 있으면 갱신할 재료가 없기 때문이다.
 * (2026-07-16: 로그인 상태이던 관리자의 영업 리드 파일 삭제·목록 요청 2건이 이 경로로
 *  Authorization 없이 나가 0ms 만에 401 을 맞았다. 화면은 에러만 뱉었고 새로고침으로만 풀렸다.)
 * 복구에 실패해도 요청은 그대로 보낸다 — 기존 동작을 막지 않고 성공률만 올린다.
 *
 * ⚠️ 데드락 주의: refreshSessionOnce 는 supabase-js 의 auth 락(navigator LockManager)을 잡는다.
 * onAuthStateChange 콜백 안에서 이 함수를 await 하면 락 교착으로 영원히 멈춘다 —
 * AuthContext.tsx 가 profile 조회를 setTimeout(…, 0) 으로 미루는 이유가 그것이니 걷어내지 말 것.
 */
export async function buildAuthHeaders(init?: RequestInit): Promise<Headers> {
  let session = (await supabase.auth.getSession()).data.session;
  if (!isSessionUsable(session)) {
    await refreshSessionOnce();
    session = (await supabase.auth.getSession()).data.session;
  }
  const headers = new Headers(init?.headers);
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }
  // 요청마다 번호표(x-run-id)를 발급해 보낸다 — 백엔드(index.ts runId 미들웨어)가 이 값을
  // 그대로 이어받아 서버 로그·Sentry 에 같은 번호로 박는다. 그 결과 화면에서 난 문제(Datadog
  // RUM·프론트 Sentry)와 서버 처리(Cloud Logging·백엔드 Sentry)를 하나의 번호로 꿸 수 있다.
  // 이미 지정돼 있으면(재시도 등 호출부가 고정한 경우) 덮지 않는다.
  if (!headers.has("x-run-id") && typeof crypto?.randomUUID === "function") {
    headers.set("x-run-id", crypto.randomUUID());
  }
  // 도도매 업체의 항목 단위 서버 가드가 "어느 화면에서 낸 요청인가"를 같은 메뉴 키로
  // 판정한다. 서버는 계정의 DB 설정과 이 경로를 함께 확인하며, 부모·일반 매장에는 영향 없음.
  if (typeof window !== "undefined") {
    headers.set("x-phoneboard-page", window.location.pathname);
  }
  return headers;
}

/**
 * Content-Type=application/json 을 언제 자동으로 붙일지 결정하는 규칙.
 *   - "truthy"      : body 가 truthy 면 (crm.ts · adminImport.ts 기존 동작)
 *   - "string-only" : body 가 string 일 때만 — FormData 등은 건드리지 않음 (policyApi.ts 기존 동작)
 *   - "always"      : 항상 (호출부가 body 유무를 이미 판단했을 때 — catalogApi.ts)
 *   - "never"       : 자동 설정 안 함 (multipart 등)
 * 어떤 모드든 호출부가 이미 Content-Type 을 지정해 뒀으면 덮지 않는다.
 */
export type ContentTypeMode = "truthy" | "string-only" | "always" | "never";

function maybeSetJsonContentType(
  headers: Headers,
  body: BodyInit | null | undefined,
  mode: ContentTypeMode,
): void {
  if (headers.has("Content-Type")) return;
  const should =
    mode === "always"
      ? true
      : mode === "truthy"
        ? Boolean(body)
        : mode === "string-only"
          ? typeof body === "string"
          : false;
  if (should) headers.set("Content-Type", "application/json");
}

/** 세션이 아직 유효한지(만료 안 됨) 판정. expires_at 은 epoch 초 단위. */
function isSessionUsable(
  session: { access_token?: string | null; expires_at?: number | null } | null | undefined,
): boolean {
  if (!session?.access_token) return false;
  // expires_at 이 없으면(드묾) 토큰 존재만으로 유효하다고 본다.
  if (typeof session.expires_at !== "number") return true;
  // 시계 오차·왕복 지연을 감안해 약간의 여유(5초)를 두고 만료를 판단한다.
  const SKEW_SEC = 5;
  return session.expires_at > Date.now() / 1000 + SKEW_SEC;
}

/**
 * 401(토큰 만료) 대응 — 세션 복구를 한 번 시도하고, 쓸 수 있는 토큰을 확보했는지 boolean 으로 돌려준다.
 *
 * 화면 진입 시 여러 API 가 동시에 옛 토큰으로 나가 한꺼번에 401 을 받을 수 있다. 그때마다
 * refreshSession 을 따로 부르면 토큰 회전이 충돌하므로, 진행 중 복구를 하나의 Promise 로 묶어
 * 모두가 같은 결과를 기다리게 한다(in-flight 공유).
 *
 * 멀티탭·동시요청에서는 리프레시 토큰 회전 충돌로 refreshSession 이 "Already Used" 류로 실패할 수
 * 있는데, 그 순간 **다른 탭이 이미 새 토큰을 저장소에 넣어 둔** 경우가 흔하다. 그래서 refreshSession
 * 의 성패와 무관하게, 마지막에 getSession 으로 저장소의 현재 세션을 다시 읽어 **만료 안 된 토큰이
 * 있으면 복구 성공**으로 친다 — "내가 갱신했거나 / 다른 손이 갱신했거나" 둘 중 하나면 살아난다.
 */
let refreshInFlight: Promise<boolean> | null = null;
function refreshSessionOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      // 1) 직접 갱신을 시도한다. 성공하면 그 새 세션이 곧 저장소에도 들어간다.
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && isSessionUsable(data.session)) return true;
      } catch {
        // 회전 충돌("Already Used") 등 — 아래 재확인으로 흡수한다.
      }
      // 2) 갱신이 실패·무효였더라도, 다른 탭이 이미 새 토큰을 넣어 뒀을 수 있다.
      //    저장소의 현재 세션을 다시 읽어 유효하면 성공으로 간주한다.
      try {
        const { data } = await supabase.auth.getSession();
        return isSessionUsable(data.session);
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * 인증 헤더를 붙여 fetch 한다. 401(토큰 만료)을 받으면 세션 복구를 한 번 거치고 **새 토큰으로
 * 그 요청을 1회 재시도**한다 — Supabase 의 백그라운드 자동 갱신과 API 호출이 겹치는 찰나에
 * 옛 토큰이 날아가 401 이 나도 사용자가 깜빡임을 못 느끼게 하는 안전장치다.
 * 복구는 refreshSessionOnce 가 담당한다: 내가 직접 갱신했거나, (멀티탭 회전 충돌로 갱신이 실패해도)
 * 다른 탭이 넣어 둔 새 토큰을 재확인해 살린다. 재시도 헤더는 buildAuthHeaders 가 다시 getSession 으로
 * 최신 토큰을 읽어 만들기 때문에, **누가 갱신했든 그 최신 토큰을 자동으로 집어** 재전송한다.
 * 재시도는 1회뿐 — 그 후에도 또 401 이면 진짜 만료이므로 그 응답을 그대로 돌려준다(→ 호출부가 throw).
 *
 * buildInit 은 헤더를 받아 RequestInit 을 만드는 콜백 — 재시도 때 새 헤더로 다시 호출되어
 * Authorization·Content-Type 이 새 토큰 기준으로 멱등하게 재구성된다.
 * (요청 body 는 string·FormData 뿐이라 재전송에 안전 — stream body 는 쓰지 않는다.)
 *
 * ⏱ 타임아웃(timeoutMs): 매 fetch 를 AbortController 로 감싸 일정 시간 내 응답이 없으면 끊는다.
 * 백엔드가 콜드스타트(Cloud Run)·5xx·네트워크 단절로 무응답일 때 fetch 가 영원히 매달려
 * 화면이 에러도 없이 무한 로딩에 갇히는 것을 막는다(2026-06-17, 로그인 데드락과 같은 뿌리 —
 * "기다림에 시간 제한이 없다"). abort 되면 fetch 가 throw → authJson 의 catch 흐름을 그대로 타
 * 5xx/네트워크와 동일하게 처리(재시도 또는 정상 실패)된다. 401 재시도도 각자 새 타임아웃을 갖는다.
 */
const DEFAULT_TIMEOUT_MS = 15000;

async function fetchWithAuthRetry(
  url: string,
  buildInit: (headers: Headers) => RequestInit,
  init?: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  // 요청 진입 순간 액션을 고정한다 — buildAuthHeaders 의 getSession(await) 사이에 사용자가
  // 다른 동작을 해 현재 액션이 바뀌어도, 이 요청은 출발 시점의 액션에 묶이게(재시도도 동일).
  const actionId = getCurrentActionId();
  // 401 복구를 기다리는 사이 화면이 바뀌어도 옛 조회가 새 화면 신호로 되살아나지 않게,
  // 요청 진입 시점의 화면 신호를 재시도까지 고정한다.
  const routeSignal = getRouteRequestSignal();
  const fetchOnce = async () => {
    const controller = new AbortController();
    // 우리가 끊었는지(타임아웃) vs 호출부가 끊었는지(react-query 취소)를 구분하는 표식 —
    // 아래 catch 에서 사용자에게 보여줄 문구를 가르는 기준이 된다.
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const headers = await buildAuthHeaders(init);
    // 사람의 한 동작이 유발한 요청들을 서버 로그에서도 한 번호로 묶기 위한 액션 번호표.
    headers.set("x-action-id", actionId);
    // 호출부가 signal 을 주면(react-query 의 쿼리 취소 등) 타임아웃 signal 과 병합한다 —
    // 어느 쪽이 먼저 abort 하든 요청이 끊긴다. 날짜 연타처럼 이미 필요 없어진 무거운 조회가
    // 네트워크·서버에 계속 매달리는 것을 호출부가 끊을 수 있게 한다.
    const builtInit = buildInit(headers);
    const method = String(builtInit.method ?? "GET").toUpperCase();
    const signals = [controller.signal];
    if (builtInit.signal) signals.push(builtInit.signal);
    // 화면 수명에 묶는 것은 읽기뿐이다. 저장·삭제·발송은 화면을 옮겼다는 이유로 중간 취소하면
    // 결과를 알 수 없는 상태가 되므로 기존 타임아웃/호출부 신호만 따른다.
    if (method === "GET" || method === "HEAD") signals.push(routeSignal);
    const signal = signals.length === 1 ? signals[0] : AbortSignal.any(signals);
    const reqInit: RequestInit = { ...builtInit, headers, signal };
    const done = recordRequest(actionId, method, url);
    // 이 요청에 실어 보낸 번호표 — 프론트 Sentry 에러를 백엔드 로그·에러와 같은 run_id로
    // 잇는 열쇠(2026-07-05). breadcrumb 으로 남겨 이후 터지는 프론트 에러에 따라붙게 하고,
    // 실패 응답은 태그로도 박아 Sentry 검색(run_id:<값>)이 프론트·백 양쪽에서 되게 한다.
    const runId = headers.get("x-run-id");
    try {
      const res = await fetch(url, reqInit);
      const len = res.headers.get("content-length");
      done(res.status, len ? Number(len) || null : null);
      Sentry.addBreadcrumb({
        category: "api",
        message: `${method} ${url}`,
        level: res.ok ? "info" : "error",
        data: { run_id: runId, action_id: actionId, status: res.status },
      });
      if (!res.ok && runId) {
        Sentry.getCurrentScope().setTag("run_id", runId);
        Sentry.getCurrentScope().setTag("action_id", actionId);
      }
      return res;
    } catch (e) {
      if (runId) Sentry.getCurrentScope().setTag("run_id", runId);
      done(null, null); // 타임아웃·네트워크 단절 — 상태 없이 실패로 기록.
      // 응답이 아예 못 온 실패는 apiError(Response 기반)를 못 거치므로 여기서 사람 말로 바꾼다.
      // 안 그러면 브라우저 원문("signal is aborted without reason")이 화면에 그대로 뜬다
      // (2026-07-28: DB 연결 고갈로 저장이 15초를 넘겼을 때 구두 관리 화면에 실제로 노출됐다).
      throw toNetworkError(e, { timedOut, method, timeoutMs });
    } finally {
      clearTimeout(timer);
    }
  };
  const res = await fetchOnce();
  if (res.status !== 401) return res;
  const refreshed = await refreshSessionOnce();
  if (!refreshed) return res;
  return fetchOnce();
}

/**
 * 인증 헤더를 붙여 요청하고, 실패하면 apiError 로 변환해 throw, 성공하면 res.json() 을 돌려준다.
 * contentTypeMode 로 4곳의 Content-Type 자동 설정 차이를 흡수한다(기본 "truthy").
 * 401 은 fetchWithAuthRetry 가 토큰 갱신 후 1회 재시도한다.
 *
 * ⏱ timeoutMs 는 authFetch(60초)·authForm(120초)과 같은 이유로 호출부가 늘릴 수 있다 —
 * 서버가 "설계상 오래 걸리는 정상 요청"으로 분류해 둔 경로(server/src/lib/request-timeout.ts
 * LONG_REQUEST_PATTERNS, 상한 165초)를 15초에 끊으면, 정상 작업이 화면에서만 실패로 보인다.
 */
export async function authJson<T>(
  url: string,
  init?: RequestInit,
  contentTypeMode: ContentTypeMode = "truthy",
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const res = await fetchWithAuthRetry(
    url,
    (headers) => {
      maybeSetJsonContentType(headers, init?.body, contentTypeMode);
      return { ...init, headers };
    },
    init,
    timeoutMs,
  );
  if (!res.ok) throw await apiError(res);
  return readJsonBody<T>(res);
}

/**
 * 성공 응답 본문을 JSON 으로 읽는다. 게이트웨이 HTML·빈 본문 등으로 못 읽으면 브라우저 원문
 * (`Unexpected token '<'…`) 대신 사람 말 ApiError 로 던진다 — 원문은 .serverMessage 에 남긴다.
 */
async function readJsonBody<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    throw new ApiError("서버 응답을 읽지 못했어요. 잠시 후 다시 시도해 주세요.", res.status, detail, null);
  }
}

/**
 * 인증 헤더를 붙여 요청하고, **raw Response 를 그대로 돌려준다** — JSON 파싱을 하지 않으므로
 * 파일 다운로드(res.blob())·텍스트(res.text()) 등 비-JSON 응답을 호출부가 직접 처리할 때 쓴다.
 * 401 자동 재시도는 fetchWithAuthRetry 를 그대로 재사용해 authJson/authForm 과 동일하게 받는다.
 * (성공/실패 판정·apiError 변환은 호출부 몫 — res.ok 를 직접 보고 처리한다.)
 *
 * 다운로드(신청서 내보내기 등)는 조회보다 클 수 있어 기본 타임아웃을 60초로 둔다 — 무응답은
 * 끊되 건수 많은 export 가 abort 되지 않게. 호출부가 timeoutMs 로 더 늘릴 수 있다.
 */
export async function authFetch(
  url: string,
  init?: RequestInit,
  timeoutMs: number = 60000,
): Promise<Response> {
  return fetchWithAuthRetry(url, (headers) => ({ ...init, headers }), init, timeoutMs);
}

/**
 * multipart 전송 — Authorization 만 싣고 Content-Type 은 브라우저(boundary 포함)에 맡긴다.
 * method 기본 POST. (policyApi.ts fetchJsonForm · crm.ts 의 이미지 업로드 패턴 공통화)
 * 401 은 fetchWithAuthRetry 가 토큰 갱신 후 1회 재시도한다.
 *
 * 타임아웃은 조회(15초)보다 길게(120초) — 정책표 엑셀 등 큰 파일 업로드+서버측 파싱이
 * 15초를 넘기 쉬워, 정상 업로드가 abort 되지 않게 한다.
 *
 * ⚠️ 업로드가 **서버에서 LLM 까지 부르는** 경로(상담 훈련장 자료 올리기)는 120초로 모자란다 —
 * 서버 상한이 180초인데 화면이 120초에 끊으면 **정상 처리가 화면에서만 실패로 보인다.**
 * 그런 호출부만 timeoutMs 로 늘려 쓴다(기본값은 그대로라 다른 업로드는 영향 없다).
 */
const UPLOAD_TIMEOUT_MS = 120000;

export async function authForm<T>(
  url: string,
  form: FormData,
  init?: Omit<RequestInit, "body">,
  timeoutMs: number = UPLOAD_TIMEOUT_MS,
): Promise<T> {
  const res = await fetchWithAuthRetry(
    url,
    (headers) => ({ method: "POST", ...init, headers, body: form }),
    init,
    timeoutMs,
  );
  if (!res.ok) throw await apiError(res);
  return readJsonBody<T>(res);
}
